import { Box, Button, Flex, Icon, Text } from "@chakra-ui/react";
import { Tooltip } from "./components/Tooltip";
import { useTranslation } from "react-i18next";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

interface StepNavigationProps {
  onNext?: () => void;
  nextLabel?: string | null;
  onPrevious?: () => void;
  isNextDisabled?: boolean;
  isNextLoading?: boolean;
  nextLoadingText?: string | null;
  nextTooltip?: string | null;
  nextDisabledReason?: string | null;
  nextIsAction?: boolean;
}

export const StepNavigation = ({
  onNext,
  nextLabel,
  onPrevious,
  isNextDisabled,
  isNextLoading,
  nextLoadingText,
  nextTooltip,
  nextDisabledReason,
  nextIsAction,
}: StepNavigationProps) => {
  const { t } = useTranslation();

  const nextButtonText = nextIsAction
    ? (nextLabel ?? t("Next"))
    : nextLabel
      ? `${t("Next")}: ${nextLabel}`
      : t("Next");

  const nextButton = (
    <Button
      variant="solid"
      colorScheme="teal"
      size="lg"
      onClick={onNext}
      isDisabled={isNextDisabled}
      isLoading={isNextLoading}
      loadingText={nextLoadingText}
      rightIcon={!nextIsAction ? <Icon as={FaChevronRight} w="12px" h="12px" /> : undefined}
      width="full"
    >
      {nextButtonText}
    </Button>
  );

  const navigation = (
    <Flex direction={{ base: "column-reverse", md: "row" }} gap="3" width="full" mt="8">
      {onPrevious && (
        <Button
          variant="outline"
          colorScheme="teal"
          size="lg"
          onClick={onPrevious}
          leftIcon={<Icon as={FaChevronLeft} w="12px" h="12px" />}
          flex={{ md: "1" }}
          width={{ base: "full", md: "auto" }}
        >
          {t("Previous")}
        </Button>
      )}
      {(onNext || (nextLabel && isNextDisabled)) && (
        nextTooltip ? (
          // Wrap in a Box so the Tooltip's pointer events fire when the
          // underlying button is disabled — disabled buttons don't dispatch hover.
          <Tooltip label={nextTooltip} hasArrow>
            <Box flex={{ md: "1" }} width={{ base: "full", md: "auto" }}>
              {nextButton}
            </Box>
          </Tooltip>
        ) : (
          <Box flex={{ md: "1" }} width={{ base: "full", md: "auto" }}>
            {nextButton}
          </Box>
        )
      )}
    </Flex>
  );

  if (!(isNextDisabled && nextDisabledReason)) return navigation;

  return (
    <Box width="full">
      {navigation}
      <Text mt="2" fontSize="sm" color="smMuted" textAlign={{ base: "center", md: "right" }}>
        {nextDisabledReason}
      </Text>
    </Box>
  );
};
